import React, { useMemo } from "react"
import type { WidgetProps } from "@rjsf/core"
import { NumberInput, TextInput } from "resha"

export function TextWidget({
    disabled,
    schema,
    value,
    autofocus,
    placeholder,
    readonly,
    onChange,
    options,
    id
}: WidgetProps) {
    const readOnly = disabled || readonly
    const examplePlaceholder = useMemo(() => {
        if (Array.isArray(schema.examples) && schema.examples.length > 0) {
            const ids = id.split("_") // in case of array items, the last value indicates the index
            const unsafeIndex = Number(ids[ids.length - 1])
            const index = isNaN(unsafeIndex) ? 0 : unsafeIndex
            return placeholder !== ""
                ? placeholder
                : String(schema.examples[index % schema.examples.length])
        }
        return placeholder
    }, [placeholder, schema, id])

    if (schema.const !== undefined) {
        return null
    }

    if (readOnly && value === undefined) {
        return <span className="text-smoke-810">No data</span>
    }

    if (schema.type === "integer" || schema.type === "number") {
        return (
            <NumberInput
                syncValue
                transparentBg
                value={value}
                minimum={schema.minimum ?? schema.exclusiveMinimum}
                maximum={schema.maximum ?? schema.exclusiveMaximum}
                readOnly={readOnly}
                type={schema.type === "integer" ? "integer" : "float"}
                onValueChange={onChange}
                className="rounded-lg border border-rhino-300"
                inputClassName="bg-transparent py-2 px-3 text-sm leading-5"
                placeholder={
                    examplePlaceholder ? `e.g. ${examplePlaceholder}` : ""
                }
            />
        )
    }

    const handleChange = (value: string) => {
        if (value === "") {
            onChange(undefined)
        } else {
            onChange(value.trimStart())
        }
    }

    return (
        <TextInput
            singleLine
            placeholder={examplePlaceholder ? `e.g. ${examplePlaceholder}` : ""}
            autoFocus={autofocus}
            readOnly={readOnly}
            value={value}
            onValueChange={handleChange}
            enableCopyTextButton={options.enableCopyTextButton === true}
            enableMarkdown={options?.enableMarkdown === true}
            inputClassName={`rounded-lg bg-transparent text-sm leading-5 ${
                readOnly && (value || "").trim() === "" ? "py-4.5 px-4.5" : ""
            } ${readOnly ? "" : "py-2 px-3"}`}
            className={`rounded-lg border border-rhino-300 ${
                readOnly ? "py-2 px-3 bg-rhino-300 opacity-60" : ""
            }`}
        />
    )
}
